import React, { useState } from 'react'
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Paper,
  Link,
  IconButton,
  Popover,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import BarChartIcon from '@mui/icons-material/BarChart'
import { LicenseInfo, DistrictInfo } from '../types'
import { formatDate } from '../helpers'

interface LicenseHeaderProps {
  lCode: string
  license: LicenseInfo
  district: DistrictInfo | null
  hasMetrics: boolean
  parentOrigin: string
  actionLoading: boolean
  onRegenerateAccessCode: () => void
}

function LicenseHeader({
  lCode,
  license,
  district,
  hasMetrics,
  parentOrigin,
  actionLoading,
  onRegenerateAccessCode,
}: LicenseHeaderProps) {
  const accessExpires = license.teacherAccessExpires
    ? formatDate(license.teacherAccessExpires)
    : ''
  const isAccessExpired =
    license.teacherAccessExpires != null &&
    license.teacherAccessExpires < Date.now()
  const daysUntilExpiry =
    license.teacherAccessExpires != null && !isAccessExpired
      ? Math.ceil(
          (license.teacherAccessExpires - Date.now()) / (24 * 60 * 60 * 1000)
        )
      : null
  let daysText = ''
  if (daysUntilExpiry !== null) {
    if (daysUntilExpiry === 0) daysText = ' (today)'
    else if (daysUntilExpiry === 1) daysText = ' (in 1 day)'
    else daysText = ` (in ${daysUntilExpiry} days)`
  }
  const [copyAnchor, setCopyAnchor] = useState<HTMLElement | null>(null)

  const showCopied = (anchor: HTMLElement) => {
    navigator.clipboard.writeText(lCode)
    setCopyAnchor(anchor)
    setTimeout(() => setCopyAnchor(null), 2000)
  }

  const showCopiedAccessCode = (anchor: HTMLElement) => {
    const code = license.teacherAccessCode || ''
    if (code) {
      navigator.clipboard.writeText(code)
      setCopyAnchor(anchor)
      setTimeout(() => setCopyAnchor(null), 2000)
    }
  }

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='flex-start'
      gap={6}
      sx={{ mb: 4 }}
    >
      <Popover
        open={Boolean(copyAnchor)}
        anchorEl={copyAnchor}
        onClose={() => setCopyAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Typography sx={{ p: 1.5, fontWeight: 600 }}>Copied!</Typography>
      </Popover>
      <Stack spacing={2} sx={{ minWidth: 0, flex: 1 }}>
        <Chip
          label={
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Typography
                component='span'
                variant='caption'
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                }}
              >
                License code
              </Typography>
              <Typography
                component='span'
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: 0.5,
                }}
              >
                {lCode}
              </Typography>
              <IconButton
                size='small'
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation()
                  showCopied(e.currentTarget)
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          }
          variant='outlined'
          color='primary'
          onClick={(e) => showCopied(e.currentTarget)}
          sx={{
            alignSelf: 'flex-start',
            cursor: 'pointer',
            backgroundColor: 'common.white',
          }}
        />
        <Typography
          variant='h3'
          component='h1'
          sx={{
            fontWeight: 700,
            // fontSize: { xs: '1.5rem', sm: '1.75rem' },
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          {license.name || `License ${lCode}`}
        </Typography>
        {district && (
          <Link
            href={`${parentOrigin}/district?o=${district.code}`}
            target='_blank'
            underline='hover'
            sx={{
              fontSize: '0.875rem',
              color: 'text.secondary',
              fontWeight: 500,
              '&:hover': { color: 'primary.main' },
            }}
          >
            {district.name}
          </Link>
        )}
      </Stack>

      <Stack spacing={2} alignItems='flex-end' sx={{ flexShrink: 0 }}>
        {hasMetrics && (
          <Button
            variant='contained'
            size='small'
            startIcon={<BarChartIcon />}
            onClick={() =>
              window.open(
                `${parentOrigin}/license-metrics?l=${lCode}`,
                '_blank'
              )
            }
          >
            Metrics
          </Button>
        )}
        <Paper
          elevation={0}
          variant='outlined'
          sx={{
            minWidth: 280,
            maxWidth: 320,
            p: 2,
            ...(isAccessExpired && {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.error.main, 0.3),
              borderLeftWidth: 4,
              borderLeftColor: 'error.main',
            }),
          }}
        >
          <Typography
            variant='overline'
            sx={{
              display: 'block',
              fontWeight: 600,
              letterSpacing: 0.5,
              color: isAccessExpired ? 'error.dark' : 'text.secondary',
              mb: 0.75,
              lineHeight: 1,
            }}
          >
            Teacher access code
          </Typography>
          <Box
            component='span'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              fontFamily: 'monospace',
              fontWeight: 700,
              fontSize: '1.125rem',
              letterSpacing: 0.5,
              color: isAccessExpired ? 'error.dark' : undefined,
              ...(isAccessExpired && {
                textDecoration: 'line-through',
              }),
            }}
          >
            {license.teacherAccessCode || 'N/A'}
            {license.teacherAccessCode && !isAccessExpired && (
              <IconButton
                size='small'
                sx={{ p: 0, ml: 0.5 }}
                onClick={(e) => showCopiedAccessCode(e.currentTarget)}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
          {accessExpires && (
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                mt: 1,
                color: isAccessExpired ? 'error.main' : 'text.secondary',
                fontSize: '0.8rem',
                ...(isAccessExpired && { fontWeight: 600 }),
              }}
            >
              Expire{isAccessExpired ? 'd' : 's'} {accessExpires}
              {daysText}
            </Typography>
          )}
          <Button
            variant='outlined'
            size='small'
            fullWidth
            disabled={actionLoading}
            onClick={onRegenerateAccessCode}
            color={isAccessExpired ? 'error' : 'primary'}
            sx={{
              mt: 2,
              fontSize: '0.75rem',
              textTransform: 'none',
              py: 0.75,
              ...(isAccessExpired && {
                bgcolor: 'error.main',
                color: 'common.white',

                '&:hover': {
                  bgcolor: 'error.dark',
                  color: 'common.white',
                },
              }),
            }}
          >
            Regenerate access code
          </Button>
        </Paper>
      </Stack>
    </Stack>
  )
}

export default React.memo(LicenseHeader)
